import React, {useState} from 'react';
import {Settings, PhoneOff, LogOut, Download, Trash2, Menu, X} from 'lucide-react';
import RoomCodeDisplay from './RoomCodeDisplay';
import ConnectionStatus from './ConnectionStatus';
import ParticipantDisplay from './ParticipantDisplay';
import ParticipantCount from "../ParticipantsList/ParticipantCount";
import useTranscriptionStore from "../../../store/transcriptionStore";
import {useNavigate, useParams} from "react-router-dom";
import toast from "react-hot-toast";

const RoomHeader = ({
                        roomCode, copiedCode, copyRoomCode,
                        userName, myChannel, isMuted,
                        otherParticipant, otherParticipantMuted, isCallConnected,
                        connectionState, rtcStatus,
                        showSettings, setShowSettings,
                        leaveRoom, handleLogout,
                        participants, clearHistory
                    }) => {
    const navigate = useNavigate();
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const {exportRoom} = useTranscriptionStore();
    const {roomCode: currentRoomCode} = useParams();

    const handleExport = (format) => {
        const data = exportRoom(currentRoomCode, format);
        if (!data) {
            toast.error('No transcriptions to export', {position: 'bottom-center'});
            return;
        }

        const blob = new Blob([data], {
            type: format === 'json' ? 'application/json' : 'text/plain'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transcriptions-${currentRoomCode}-${new Date().toISOString().split('T')[0]}.${format}`;
        a.click();
        URL.revokeObjectURL(url);

        toast.success(`Exported as ${format.toUpperCase()}`, {position: 'bottom-center'});
        setShowMobileMenu(false);
    };


    return (
        <>
            {/* Desktop Header */}
            <div
                className="hidden lg:flex bg-white shadow-md border-b border-white/10 px-6 h-24 items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-6 xl:gap-8">
                    <img
                        src={otherParticipant ? "/img/logo-mini.png" : "/img/logo.png"}
                        alt="Vulavula Co-Pilot Logo"
                        className="h-18 lg:h-24 w-auto object-contain cursor-pointer"
                        onClick={() => navigate('/')}
                    />
                    <div
                        className="w-px h-10 bg-gradient-to-b from-transparent via-black/20 to-transparent"></div>


                    <RoomCodeDisplay
                        roomCode={roomCode}
                        copiedCode={copiedCode}
                        copyRoomCode={copyRoomCode}
                        participants={participants}
                        myChannel={myChannel}
                    />

                    <div className="w-px h-10 bg-gradient-to-b from-transparent via-black/20 to-transparent"></div>

                    <ParticipantDisplay
                        userName={userName}
                        myChannel={myChannel}
                        isMuted={isMuted}
                        otherParticipant={otherParticipant}
                        otherParticipantMuted={otherParticipantMuted}
                        isCallConnected={isCallConnected}
                    />

                    <div className="w-px h-10 bg-gradient-to-b from-transparent via-black/20 to-transparent"></div>

                    {/*<ParticipantCount participants={participants}/>*/}

                    {/*<div className="w-px h-10 bg-gradient-to-b from-transparent via-black/20 to-transparent"></div>*/}

                    <ConnectionStatus connectionState={connectionState} rtcStatus={rtcStatus}/>

                    <div className="w-px h-10 bg-gradient-to-b from-transparent via-black/20 to-transparent"></div>



                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleExport('json')}
                        className="p-2.5 bg-black/5 hover:bg-black/10 text-zinc-600 hover:text-black rounded-lg transition-all"
                        title="Export as JSON"
                    >
                        <Download className="w-4 h-4"/>
                    </button>

                    <button
                        onClick={clearHistory}
                        className="p-2.5 bg-black/5 hover:bg-red-500/20 text-zinc-600 hover:text-red-300 rounded-lg transition-all"
                        title="Clear history"
                    >
                        <Trash2 className="w-4 h-4"/>
                    </button>

                    <button
                        onClick={() => setShowSettings(!showSettings)}
                        className={`p-2.5 rounded-lg transition-all duration-300 ${
                            showSettings
                                ? 'bg-black/15 text-black shadow-lg scale-105'
                                : 'bg-black/5 hover:bg-black/10 text-zinc-600 hover:text-black'
                        }`}
                        title="Settings"
                    >
                        <Settings className="w-5 h-5"/>
                    </button>

                    <button
                        onClick={leaveRoom}
                        className="px-4 py-2.5 bg-gradient-to-r from-zinc-800/90 to-zinc-900/90 hover:from-zinc-700 hover:to-zinc-800 text-white rounded-lg transition-all flex items-center gap-2 border border-white/10 hover:shadow-lg group"
                    >
                        <PhoneOff className="w-4 h-4 group-hover:rotate-12 transition-transform"/>
                        <span className="text-sm">Leave</span>
                    </button>

                    <button
                        onClick={handleLogout}
                        className="p-2.5 bg-white/5 hover:bg-red-500/20 hover:text-red-300 rounded-lg transition-all group"
                        title="Logout"
                    >
                        <LogOut
                            className="w-5 h-5 text-zinc-600 group-hover:text-red-300 group-hover:rotate-180 transition-all duration-500"/>
                    </button>
                </div>
            </div>

            {/* Mobile/Tablet Header */}
            <div
                className="lg:hidden glass-panel border-b border-white/10 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                    <button
                        onClick={copyRoomCode}
                        className="px-2 sm:px-3 py-1.5 sm:py-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 rounded-lg hover:from-orange-500/30 hover:to-red-500/30 transition-all active:scale-95"
                    >
                        <div className="text-[10px] sm:text-xs text-orange-300 font-medium">
                            {copiedCode ? 'Copied!' : roomCode}
                        </div>
                    </button>
                    <div className="hidden sm:block">
                        <ConnectionStatus connectionState={connectionState} rtcStatus={rtcStatus} compact/>
                    </div>
                </div>

                <button
                    onClick={() => setShowMobileMenu(!showMobileMenu)}
                    className="p-2 bg-white/5 hover:bg-white/10 rounded-lg transition-all"
                >
                    {showMobileMenu ? (
                        <X className="w-5 h-5 text-white"/>
                    ) : (
                        <Menu className="w-5 h-5 text-white"/>
                    )}
                </button>
            </div>

            {/* Mobile Menu Dropdown */}
            {showMobileMenu && (
                <div
                    className="lg:hidden glass-panel border-b border-white/10 px-3 sm:px-4 py-3 space-y-3 animate-slide-down">
                    {/* Participant Info */}
                    <div className="flex items-center justify-between pb-3 border-b border-white/10">
                        <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                                isCallConnected ? 'bg-green-400' : 'bg-zinc-600'
                            }`}></div>
                            <span className="text-sm text-white font-medium">{userName}</span>
                        </div>
                        <ParticipantCount participants={participants} compact/>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            onClick={() => {
                                setShowSettings(!showSettings);
                                setShowMobileMenu(false);
                            }}
                            className="px-3 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg transition-all flex items-center justify-center gap-2 text-sm text-white"
                        >
                            <Settings className="w-4 h-4"/>
                            <span>Settings</span>
                        </button>

                        <button
                            onClick={() => {
                                handleExport('json');
                            }}
                            className="px-3 py-2.5 bg-white/5 hover:bg-white/10 rounded-lg transition-all flex items-center justify-center gap-2 text-sm text-white"
                        >
                            <Download className="w-4 h-4"/>
                            <span>Export</span>
                        </button>

                        <button
                            onClick={() => {
                                clearHistory();
                                setShowMobileMenu(false);
                            }}
                            className="px-3 py-2.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all flex items-center justify-center gap-2 text-sm text-red-300"
                        >
                            <Trash2 className="w-4 h-4"/>
                            <span>Clear</span>
                        </button>

                        <button
                            onClick={() => {
                                leaveRoom();
                                setShowMobileMenu(false);
                            }}
                            className="px-3 py-2.5 bg-zinc-800/90 hover:bg-zinc-700 rounded-lg transition-all flex items-center justify-center gap-2 text-sm text-white"
                        >
                            <PhoneOff className="w-4 h-4"/>
                            <span>Leave</span>
                        </button>
                    </div>

                    {/* Logout Button */}
                    <button
                        onClick={() => {
                            handleLogout();
                            setShowMobileMenu(false);
                        }}
                        className="w-full px-3 py-2.5 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-all flex items-center justify-center gap-2 text-sm text-red-300 border border-red-500/20"
                    >
                        <LogOut className="w-4 h-4"/>
                        <span>Logout</span>
                    </button>
                </div>
            )}
        </>

    );
};

export default RoomHeader;